// Native
import { useParams } from "react-router"

// Components
import { Avatar, Button, ButtonGroup, Divider, Flex, Grid, GridItem, Stack, Tab, TabList, TabPanel, TabPanels, Tabs, Text, useDisclosure, useToast } from "@chakra-ui/react"
import { Alert, Breadcrumb, Clipboard, Container, Dev, Icon, IconButton, Label, Pressable, Scope, Show } from "components"

// Config
import { Mask, Moment } from "config"

// Hooks
import { useEmployee } from "hooks"

// Screens
import General from "./general"
import Roles from "./roles"
import Password from "./password"
import Block from "./block"
import Edit from "./edit"
import Trash from "./trash"
import Warehouse from "./warehouse"
import OrdersVisibility from "./orders-visibility"
import { useEffect, useRef, useState } from "react"
import { API } from "config"
import Files from "./files"

export default function Employee(){

    const params = useParams()
    const toast = useToast()
    const avatarFileInputRef = useRef()
    const [ avatarFile, setAvatarFile ] = useState()
    const [ loading, setLoading ] = useState(false)
    const employee = useEmployee(params.id)
    const roles = useDisclosure()
    const password = useDisclosure()
    const block = useDisclosure()
    const edit = useDisclosure()
    const trash = useDisclosure()
    const warehouse = useDisclosure()
    const ordersVisibility = useDisclosure()

    const handleSubmit = async () => {
        if(!avatarFile) return
        setLoading(true)

        const form = new FormData()
        form.append('avatar_file', avatarFile)
        form.append('_method', 'put')

        try{
            await API.post(`/employees/${employee.data?.id}`, form, { headers: { "Content-Type": "multipart/form-data" } })
            toast({ title: 'Imagem atualizada', status: 'success' })
            setAvatarFile(false)
            employee.refetch()
        }catch(err){
            toast({ title: 'Não foi possível atualizar a imagem.', description: err?.response?.data?.message, status: 'warning' })
        }

        setLoading(false)
    }

    useEffect(() => {
        if(avatarFile) handleSubmit()
    }, [avatarFile])

    return(
    <>
    <Container>

        <Grid templateColumns="50% 50%" alignItems="center">
            
            {/* Breadcrumbs */}
            <GridItem><Breadcrumb prev={{ path: '/employees', label: 'Funcionários' }} current="Perfil do funcionário" /></GridItem>

            {/* Actions */}
            <GridItem display="flex" justifyContent="flex-end">
                <ButtonGroup size="xs" isDisabled={employee.isFetching}>
                    <IconButton icon="refresh" isLoading={employee.isFetching} onClick={employee.refetch} label="Atualizar" />
                    <Button children="Editar" leftIcon={<Icon name="edit" />} onClick={edit.onOpen} />
                    <Scope role="employees_roles">
                        <IconButton icon="roles" colorScheme="purple" onClick={roles.onOpen} label="Permissões" />
                        <Show visible={employee.data?.roles?.includes('warehouse') || employee.data?.roles?.includes('*')}>
                            <IconButton icon="warehouse" colorScheme="blue" label="Expedição" onClick={warehouse.onOpen} />
                        </Show>
                    </Scope>
                    <Scope role="orders_manage_visibility">
                        <IconButton icon="orders" colorScheme="orange" onClick={ordersVisibility.onOpen} label="Visibilidade de pedidos" />
                    </Scope>
                    <IconButton icon="block" colorScheme="orange" onClick={block.onOpen} label="Bloquear" />
                    <IconButton icon="password" colorScheme="orange" onClick={password.onOpen} label="Redefinir senha" />
                    <IconButton icon="trash" colorScheme="red" onClick={trash.onOpen} label="Excluir" />
                    <Dev data={employee.data} />
                </ButtonGroup>
            </GridItem>

        </Grid>

        <Divider my={4} />

        <Grid templateColumns="20% 80%" gap={6}>

            <GridItem>
                <Stack spacing={4}>

                    <Flex direction="column" alignItems="center" justifyContent="center">
                        <input ref={avatarFileInputRef} type="file" accept="image/*" onChange={e => setAvatarFile(e.target.files[0])} style={{ display: 'none' }} />
                        <Pressable isDisabled={loading} label="Clique para alterar o avatar" onClick={() => avatarFileInputRef.current.click()}><Avatar src={employee.data?.avatar_url} size="xl" mb={2} name={employee.data?.name} /></Pressable>
                        <Text textAlign="center" textTransform="capitalize" fontSize="md" children={employee.data?.name} />
                        <Text fontSize="small" color="GrayText" children={employee.data?.tagline} />
                    </Flex>

                    <Divider />

                    <Label label="Nome completo:" children={employee.data?.name} textTransform="capitalize" />
                    <Label label="Data de nascimento:" children={!!employee.data?.dob && Moment(employee.data?.dob).format('LL')} />

                    <Divider />

                    <Label label="Email:" children={<Clipboard children={employee.data?.email} />} />
                    <Label label="Telefone:" children={Mask.mobile(employee.data?.mobile)} />

                    <Divider />

                    <Label label="Visto por último:" children={Moment(employee.data?.last_access).fromNow()} />
                    <Label label="IP do último acesso:" children={employee.data?.last_ip} />

                    <Divider />

                    <Label label="ID do funcionário:" fontSize="xs" children={<Clipboard children={employee.data?.id} />} />

                </Stack>
            </GridItem>

            <GridItem>

                <Stack spacing={2} mb={2}>
                    { !!employee.data?.blocked_at && <Alert title="Acesso bloqueado" status="error">Este usuário foi bloqueado desde <b>{Moment(employee.data?.blocked_at).calendar()} pelo motivo: <i>"{employee.data?.blocked_reason}"</i></b></Alert> }
                    <Scope role="orders_manage_visibility">
                        { !!employee.data?.options?.orders_visible_from && <Alert title="Visibilidade de pedidos restrita" status="warning">Este funcionário visualiza apenas pedidos criados a partir de <b>{Moment(employee.data.options.orders_visible_from).format('LL')}</b>.</Alert> }
                    </Scope>
                </Stack>

                <Tabs isLazy size="sm" variant="enclosed" colorScheme="orange">
                    <TabList>
                        <Tab children="Visão Geral" />
                        <Tab children="Arquivos" />
                    </TabList>
                    <TabPanels>
                        <TabPanel><General data={employee} /></TabPanel>
                        <TabPanel><Files data={employee.data} /></TabPanel>
                    </TabPanels>
                </Tabs>

            </GridItem>

        </Grid>

    </Container>

    {/* Screens */}
    <Roles {...roles} data={employee.data} />
    <Password {...password} data={employee.data} />
    <Block {...block} data={employee.data} onSuccess={employee.refetch} />
    <Edit {...edit} data={employee.data} onSuccess={employee.refetch} />
    <Trash {...trash} data={employee.data} />
    <Warehouse {...warehouse} data={employee.data} />
    <OrdersVisibility {...ordersVisibility} data={employee.data} onSuccess={employee.refetch} />
    </>
    )
}